// src/hooks/useSystemPreferences.ts
import { useEffect } from 'react';
import { getSystemPreferences, SystemPreferences } from '../features/admin/services/SystemPreferencesAPI';
import { AppLogger } from '../utils/loggers';

/**
 * Font family mapping from database values to actual CSS font-family strings
 */
const FONT_FAMILY_MAP: Record<string, string> = {
  // System Fonts
  // 'inter' is a stored DB value; the Inter webfont is no longer shipped
  // (unhinted, rendered soft on standard-DPI Windows), so it resolves to
  // the system stack.
  'inter': "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  'roboto': "'Roboto', sans-serif",
  'open-sans': "'Open Sans', sans-serif",
  'system-ui': "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  
  // Document Fonts
  'times-new-roman': "'Times New Roman', 'Georgia', serif",
  'garamond': "'Garamond', 'Georgia', serif",
  'century-schoolbook': "'Century Schoolbook', 'Georgia', serif",
  'georgia': "'Georgia', serif",
};

/**
 * Font size scale mapping from database values to CSS scale multipliers
 */
const FONT_SCALE_MAP: Record<string, number> = {
  'small': 0.9,
  'medium': 1.0,
  'large': 1.1,
  'x-large': 1.25,
};

/**
 * Apply system preferences to the DOM
 */
function applyPreferencesToDOM(preferences: SystemPreferences): void {
  const root = document.documentElement;
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isEditorRoute = /\/(documents\/[^/]+\/editor|dms\/editor\/[^/]+)/.test(path);

  // Apply system font
  const systemFontFamily = FONT_FAMILY_MAP[preferences.system_font] || FONT_FAMILY_MAP['inter'];
  root.style.setProperty('--lex-font-system', systemFontFamily);
  AppLogger.preferences.fontApplied(preferences.system_font, systemFontFamily);

  // Apply document font only outside editor routes (existing docs use DB font)
  if (!isEditorRoute) {
    const documentFontFamily = FONT_FAMILY_MAP[preferences.document_font] || FONT_FAMILY_MAP['times-new-roman'];
    root.style.setProperty('--lex-font-document', documentFontFamily);
    AppLogger.preferences.fontApplied(preferences.document_font, documentFontFamily, { type: 'document' });
  } else {
    AppLogger.preferences.fontSkipped('Skipping document font on editor route (DB font will be used)');
  }

  // Font scale is now driven by per-user preferences; keep the default available for merge logic
  const fontScale = FONT_SCALE_MAP[preferences.font_size_scale] || 1.0;
  root.style.setProperty('--lex-font-scale', fontScale.toString());
  AppLogger.preferences.scaleApplied(preferences.font_size_scale, `${fontScale}x`);

  // Future: Apply other preferences (contrast mode, measurement units, etc.)
}

/**
 * Hook to load and apply firm-wide system preferences
 * 
 * This hook runs on app initialization and applies preferences dynamically
 * to the entire application via CSS variables and root styles.
 * 
 * Usage: Add to App.tsx or main layout component
 * 
 * @example
 * ```tsx
 * function App() {
 *   useSystemPreferences();
 *   return <Router>...</Router>;
 * }
 * ```
 */
export function useSystemPreferences(): void {
  useEffect(() => {
    let mounted = true;

    const loadAndApplyPreferences = async () => {
      try {
        AppLogger.preferences.loading();
        const preferences = await getSystemPreferences();
        
        if (!mounted) return;

        AppLogger.preferences.loaded({ preferences: preferences as unknown as Record<string, unknown> });
        applyPreferencesToDOM(preferences);

        // Firm appearance defaults (theme, accent, surfaces, density, scale,
        // contrast) are not seeded here: the server merges them into the
        // user's preferences payload, which useUserPreferencesSync loads.
        AppLogger.preferences.applied();
      } catch (error) {
        AppLogger.preferences.fontSkipped(`Failed to load preferences: ${error}`);
        // Fail silently - defaults in CSS will be used
      }
    };

    loadAndApplyPreferences();

    // Listen for custom event when preferences are saved
    const handlePreferencesUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<SystemPreferences>;
      if (customEvent.detail) {
        AppLogger.preferences.loaded({ updated: true, preferences: customEvent.detail });
        applyPreferencesToDOM(customEvent.detail);
      }
    };

    window.addEventListener('system-preferences-updated', handlePreferencesUpdated);

    return () => {
      mounted = false;
      window.removeEventListener('system-preferences-updated', handlePreferencesUpdated);
    };
  }, []);
}

/**
 * Helper function to trigger preference reapplication after save
 * 
 * Call this after successfully saving preferences to immediately
 * apply changes without page refresh.
 * 
 * @param preferences - The updated preferences
 */
export function triggerPreferencesUpdate(preferences: SystemPreferences): void {
  const event = new CustomEvent('system-preferences-updated', {
    detail: preferences,
  });
  window.dispatchEvent(event);
}
